/*
 * shrine-main.js — restored core globals
 * ------------------------------------------------------------------
 * The theme's original shrine-main.js was missing from /assets (404),
 * which left these global helpers undefined. That broke any component
 * that calls them on init — most visibly the product media gallery
 * (media-gallery.js line 17 calls debounce() in its constructor, so
 * the whole <media-gallery> element failed to initialize and thumbnail
 * clicks did nothing).
 *
 * This file defines ONLY the standard helper functions the theme's
 * other scripts expect on the global scope. These are the canonical
 * Shrine/Dawn implementations (same signatures the theme calls with).
 * Replace this file with the official shrine-main.js from your Shrine
 * Pro purchase when convenient — it is a superset of these helpers.
 * ------------------------------------------------------------------
 */

/* Debounce: run `fn` only after `wait` ms of no calls. Used by
   media-gallery.js (slideChanged, 500ms) and quantity/note inputs. */
function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* Throttle: run `fn` at most once per `delay` ms. */
function throttle(fn, delay) {
  let last = 0;
  let timer;
  return (...args) => {
    const now = Date.now();
    const remaining = delay - (now - last);
    if (remaining <= 0) {
      clearTimeout(timer);
      timer = null;
      last = now;
      fn.apply(this, args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
}

/* Return all focusable elements within a container. */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/* Focus-trap state + handlers, shared across drawers/modals/search. */
const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code?.toUpperCase() !== 'TAB') return; // only Tab key

    // Shift + Tab off the first element -> wrap to last
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/* Close/blur on Escape; used by menus and expandable UI. */
function onKeyUpEscape(event) {
  if (event.code?.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

/* Pause every playing media on the page (video/iframe/product-model).
   Called by the gallery when switching the active slide. */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow?.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow?.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/* Expose on window so scripts that reference them explicitly resolve,
   and so nothing relies solely on non-module global hoisting. */
window.debounce = debounce;
window.throttle = throttle;
window.getFocusableElements = getFocusableElements;
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.onKeyUpEscape = onKeyUpEscape;
window.pauseAllMedia = pauseAllMedia;

/* Safe fallback so media-gallery.js announceLiveRegion() never throws
   if the theme's a11y strings aren't injected. Does not override an
   existing definition. */
window.accessibilityStrings = window.accessibilityStrings || {};
if (!window.accessibilityStrings.imageAvailable) {
  window.accessibilityStrings.imageAvailable = 'Image [index] is now available in gallery view';
}
